'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'member';
  status: 'active' | 'invited';
  joinedAt: string;
}

const mockMembers: TeamMember[] = [
  { id: 'tm-001', name: 'Marcus Allen', email: 'marcus@mailflow.io', role: 'admin', status: 'active', joinedAt: 'Jan 1, 2026' },
  { id: 'tm-002', name: 'Priya Sharma', email: 'priya@mailflow.io', role: 'member', status: 'active', joinedAt: 'Mar 15, 2026' },
  { id: 'tm-003', name: 'Daniel Okonkwo', email: 'daniel@mailflow.io', role: 'member', status: 'active', joinedAt: 'May 2, 2026' },
  { id: 'tm-004', name: 'Sofia Reyes', email: 'sofia@mailflow.io', role: 'member', status: 'invited', joinedAt: 'Jul 30, 2026' },
];

export default function TeamMembersSettings() {
  const [members, setMembers] = useState<TeamMember[]>(mockMembers);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');

  const handleInvite = () => {
    if (!inviteEmail) return;
    // Backend integration point: POST /api/team/invite
    const newMember: TeamMember = {
      id: `tm-${Date.now()}`,
      name: inviteEmail.split('@')[0],
      email: inviteEmail,
      role: inviteRole,
      status: 'invited',
      joinedAt: 'Jul 31, 2026',
    };
    setMembers((prev) => [...prev, newMember]);
    toast.success(`Invitation sent to ${inviteEmail}`);
    setInviteOpen(false);
    setInviteEmail('');
  };

  const handleRemove = (id: string) => {
    setMembers((prev) => prev.filter((m) => m.id !== id));
    toast.success('Team member removed');
  };

  const handleRoleChange = (id: string, role: 'admin' | 'member') => {
    setMembers((prev) => prev.map((m) => m.id === id ? { ...m, role } : m));
    toast.success('Role updated');
  };

  return (
    <div className="space-y-6">
      <div className="card p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="section-header">Team Members</h2>
            <p className="text-sm text-muted-foreground mt-0.5">{members.length} members on your plan</p>
          </div>
          <button onClick={() => setInviteOpen(true)} className="btn-primary">
            <Plus size={15} /> Invite Member
          </button>
        </div>

        <div className="space-y-2">
          {members.map((m) => (
            <div key={m.id} className="flex items-center gap-4 p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-primary">
                  {m.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{m.name}</p>
                <p className="text-xs text-muted-foreground truncate">{m.email}</p>
              </div>
              <div className="flex items-center gap-2">
                {m.status === 'invited' && (
                  <Badge variant="warning">Invited</Badge>
                )}
                <select
                  value={m.role}
                  onChange={(e) => handleRoleChange(m.id, e.target.value as 'admin' | 'member')}
                  disabled={m.id === 'tm-001'}
                  className="text-xs border border-border rounded-lg px-2 py-1.5 bg-card text-foreground disabled:opacity-50"
                >
                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                </select>
                {m.id !== 'tm-001' && (
                  <button
                    onClick={() => handleRemove(m.id)}
                    title="Remove member"
                    className="w-7 h-7 rounded flex items-center justify-center hover:bg-red-50 transition-all"
                  >
                    <Trash2 size={13} className="text-red-500" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)} title="Invite Team Member" size="sm">
        <div className="p-6 space-y-4">
          <div>
            <label className="label-base" htmlFor="inviteEmailField">Email Address</label>
            <input
              id="inviteEmailField"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="input-base"
              placeholder="colleague@company.com"
            />
          </div>
          <div>
            <label className="label-base" htmlFor="inviteRoleField">Role</label>
            <p className="text-xs text-muted-foreground mb-1">Admins can manage billing, domains, and team members</p>
            <select
              id="inviteRoleField"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
              className="input-base"
            >
              <option value="member">Member — can create and send campaigns</option>
              <option value="admin">Admin — full account access</option>
            </select>
          </div>
          <div className="flex gap-3 justify-end pt-2 border-t border-border">
            <button onClick={() => setInviteOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleInvite} className="btn-primary">Send Invitation</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}